/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/KubeConfigAuthComponent" />
import * as React from "react";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ObservableValue } from "VSS/Core/Observable";
import { TextField } from "VSSUI/TextField";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import * as Utils_String from "VSS/Core/Util/String";
import { InputDataType } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { EndpointsEditorPanelUtils, cloneEndpoint } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { KubernetesClusterHelper } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointHelper";
import { KubernetesCluster, KubernetesClusterData } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/KubernetesEndpointContracts";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { InputDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointEditorBaseComponent, IEndpointEditorBaseComponentState, IEndpointEditorBaseComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorBaseComponent";
import { Dropdown } from "VSSUI/Dropdown";
import { ListSelection } from "VSSUI/List";

export interface IKubeConfigAuthComponentProps extends IEndpointEditorBaseComponentProps {
    updateValidAndDirtyStateChange: (isValid?: boolean, isDirty?: boolean) => void;
}

export class KubeConfigAuthComponent extends EndpointEditorBaseComponent<IKubeConfigAuthComponentProps, IEndpointEditorBaseComponentState>{

    constructor(props: IKubeConfigAuthComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = cloneEndpoint(this.props.draftEndpoint);
        if (this.props.isUpdateForm) {
            this._resetEndpointState();
        }
        this._kubernetesClusterData = null;

        this._inputDetailsList = [
            {
                inputId: this._kubeConfigInputId,
                initialValue: props.draftEndpoint.authorization.parameters.kubeConfig,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                isConfidential: true,
                isUpdate: this.props.isUpdateForm
            },
            {
                inputId: this._clusterContextInputId,
                initialValue: props.draftEndpoint.authorization.parameters.clusterContext,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                isConfidential: true,
                isUpdate: this.props.isUpdateForm
            }
        ];

        if (this._shouldShowUrlInput()) {
            this._inputDetailsList.push(
                {
                    inputId: this._endpointUrlInputId,
                    initialValue: props.draftEndpoint.url,
                    inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.Uri, true),
                    isConfidential: false,
                    isUpdate: this.props.isUpdateForm
                }
            );
        }
    }

    public componentDidMount() {
        this.addInputs(this._inputDetailsList);
    }

    public componentWillReceiveProps(props: IKubeConfigAuthComponentProps) {
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
        if (this.props.isUpdateForm) {
            this._resetEndpointState();
        }
    }

    public componentWillUnmount() {
        let currentInputIds: string[] = [];
        this._inputDetailsList.forEach((input) => currentInputIds.push(input.inputId));
        this.removeInputs(currentInputIds);
    }

    public _resetEndpointState() {
        if (this._draftServiceEndpoint.authorization.parameters.kubeconfig === null) {
            this._draftServiceEndpoint.authorization.parameters.kubeConfig = this._draftServiceEndpoint.authorization.parameters.kubeconfig;
            delete this._draftServiceEndpoint.authorization.parameters.kubeconfig;
        }
    }

    public render(): React.ReactNode {
        let urlObservable: ObservableValue<string> = new ObservableValue(this._draftServiceEndpoint.url || "");
        let kubeConfig = this._draftServiceEndpoint.authorization.parameters.kubeConfig || "";
        let kubeConfigObservable: ObservableValue<string> = new ObservableValue(kubeConfig);
        return (
            <React.Fragment>
                {this._shouldShowUrlInput() &&
                    <FormItem
                        label={Resources.ServerUrl}
                        className="default-input-margin-top"
                        message={this._getErrorMessage(this._endpointUrlInputId)}
                        error={this._hasError(this._endpointUrlInputId)}>
                        <TextField
                            value={urlObservable}
                            inputId={this._endpointUrlInputId}
                            onChange={(event, value) => {
                                urlObservable.value = value;
                            }}
                            onBlur={() => this._handleUrlChange(urlObservable.value)}
                        />
                    </FormItem>
                }
                <FormItem
                    label={Resources.KubeConfig}
                    className="default-input-margin-top"
                    message={this._getErrorMessage(this._kubeConfigInputId)}
                    error={this._hasError(this._kubeConfigInputId)}>
                    <TextField
                        value={kubeConfigObservable}
                        placeholder={this.props.isUpdateForm ? "******" : ""}
                        ariaRoleDescription={Resources.KubeConfigCopyPasteText}
                        multiline={true}
                        rows={4}
                        onChange={(event, value) => {
                            kubeConfigObservable.value = value;
                            this._handleKubeConfigChange(kubeConfigObservable.value);
                        }}
                    />
                    <span className="secondary-text fontSizeS">{Resources.KubeConfigCopyPasteText}</span>
                </FormItem>
                <FormItem
                    label={Resources.ClusterContext}
                    className="default-input-margin-top"
                    message={this._getErrorMessage(this._clusterContextInputId)}
                    error={this._hasError(this._clusterContextInputId)}>
                    <Dropdown
                        disabled={!this._draftServiceEndpoint.authorization.parameters.kubeConfig}
                        items={this._getClusterContextList()}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, selection: any): void => {
                            this._handleClusterContextChange(selection.text);
                        }}
                        selection={this._clusterContextSelection}
                    />
                </FormItem>
                <Checkbox
                    className="default-input-margin-top"
                    checked={this._draftServiceEndpoint.data.acceptUntrustedCerts === "true"}
                    label={Resources.AcceptUntrustedCert}
                    onChange={(event, checked) => {
                        this._draftServiceEndpoint.data.acceptUntrustedCerts = checked.toString();
                        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                    }}
                />
            </React.Fragment>
        );
    }

    private _populateClusterContextOptions(kubeConfig: string, clusterContext: string) {
        if (!!kubeConfig) {
            KubernetesClusterHelper.getClusterContexts(kubeConfig, clusterContext).then((kubernetesClusterData: KubernetesClusterData) => {
                this._kubernetesClusterData = kubernetesClusterData;

                if (kubernetesClusterData.selectedCluster.context) {
                    this._handleClusterContextChange(kubernetesClusterData.selectedCluster.context);
                }

                if (kubernetesClusterData.selectedCluster.url) {
                    this._handleUrlChange(kubernetesClusterData.selectedCluster.url);
                }

                this.props.updateDraftEndpointState(this._draftServiceEndpoint);
            }, (error: any) => {
                this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                this.setInputError(this._kubeConfigInputId, Utils_String.localeFormat(Resources.KubeConfigParsingFailed, error));
            });
        }
    }

    private _hasError(inputId: string): boolean {
        return (!!this.getInputError(inputId));
    }

    private _getErrorMessage(inputId: string): string {
        return this.getInputError(inputId) || "";
    }

    private _shouldShowUrlInput = (): boolean => {
        return this.props.isUpdateForm && this.props.draftEndpoint.data.clusterContext === "";
    }

    private _getClusterContextList = (): string[] => {
        if (!!this._kubernetesClusterData && !!this._kubernetesClusterData.clusterList) {
            let clusterContextList = this._kubernetesClusterData.clusterList.map((cluster: KubernetesCluster) => cluster.context);
            
            if (!!this._draftServiceEndpoint.authorization.parameters.clusterContext) {
                let selectedIndex = clusterContextList.findIndex((context) => context === this._draftServiceEndpoint.authorization.parameters.clusterContext);
                this._clusterContextSelection.select(selectedIndex >= 0 ? selectedIndex : undefined);
            }
            else {
                this._clusterContextSelection.select(0);
            }
            return clusterContextList;
        }
        return [];
    }

    private _handleUrlChange = (value: any) => {
        this._draftServiceEndpoint.url = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        if (this._shouldShowUrlInput()) {
            this.validateInputState(this._endpointUrlInputId, value);
        }
    }

    private _handleKubeConfigChange = (value: any) => {
        this._draftServiceEndpoint.authorization.parameters.kubeConfig = value;
        this._draftServiceEndpoint.authorization.parameters.clusterContext = "";
        this._populateClusterContextOptions(value, this._draftServiceEndpoint.authorization.parameters.clusterContext);
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._kubeConfigInputId, value);
    }

    private _handleClusterContextChange = (value: any) => {
        this._draftServiceEndpoint.authorization.parameters.clusterContext = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._clusterContextInputId, value);
    }

    protected handleValidAndDirtyStateChange(isValid?: boolean, isDirty?: boolean) {
        this.props.updateValidAndDirtyStateChange(isValid, isDirty);
    }

    private _endpointUrlInputId = "endpoint-url";
    private _kubeConfigInputId = "kube-config";
    private _clusterContextInputId = "cluster-context";
    private _draftServiceEndpoint: ServiceEndpoint;
    private _kubernetesClusterData: KubernetesClusterData;
    private _inputDetailsList: InputDetails[];
    private _clusterContextSelection: ListSelection = new ListSelection({ selectOnFocus: false, multiSelect: false });
}
